import { Link } from "react-router-dom";
import { useTheme } from "@/lib/theme";
import { useSiteSettings, resolveAssetUrl } from "@/lib/siteSettings";

/**
 * Brand logo. White-label aware: when the admin configures a custom logo in
 * Public Site CMS → Brand & Theme, it is used everywhere the logo appears
 * (public header/footer, auth and dashboard shells — deliberate shared
 * branding). With no custom asset, the compiled Functional LAB PNGs render,
 * so the default appearance is unchanged.
 */

const SIZES = {
  mark:     { sm: 26, md: 34, lg: 44, xl: 60 },
  wordmark: { sm: 30, md: 40, lg: 56, xl: 76 },
  full:     { sm: 46, md: 64, lg: 92, xl: 130 },
};
const ASPECT = { mark: 1, wordmark: 3.72, full: 2.82 };

export default function Logo({ variant = "wordmark", size = "md", light = null, to = null, className = "" }) {
  const { theme } = useTheme();
  const { brand } = useSiteSettings();
  const useLight = light === null ? theme === "light" : light;

  const name = brand?.clinicName || "Functional LAB";
  const h = SIZES[variant][size];

  // Configured override takes priority.
  let configured = "";
  if (variant === "mark") configured = brand?.logoMarkUrl || brand?.logoUrl || "";
  else configured = (useLight && brand?.logoLightUrl) ? brand.logoLightUrl : (brand?.logoUrl || "");

  const themeSuffix = useLight ? "light" : "dark";
  const nameByVariant = {
    mark: `logo-${themeSuffix}.png`,
    wordmark: `logo-${themeSuffix}-wordmark.png`,
    full: `logo-${themeSuffix}-full.png`,
  };
  const defaultSrc = `/brand/${nameByVariant[variant]}`;
  const src = configured ? resolveAssetUrl(configured) : defaultSrc;
  // Custom assets have unknown aspect — keep height fixed, width auto.
  const imgStyle = configured
    ? { height: h, width: "auto", maxHeight: h, display: "block" }
    : { height: h, width: Math.round(h * ASPECT[variant]), display: "block" };

  const img = <img src={src} alt={name} style={imgStyle} draggable="false" />;

  return (
    <span className={`inline-flex items-center align-middle select-none ${className}`} role="img" aria-label={name} data-testid="brand-logo">
      {to ? <Link to={to} className="inline-flex items-center">{img}</Link> : img}
    </span>
  );
}
